import { ToolSettingsSchema, ToolSettingsValues, ToolSettingDefinition, ToolSettingValue, ToolSettingsChangeEvent, type LibraryItemDefinition } from '../personas/ToolSettings';
import { getUserDarkModeProperty } from '../../modules/Properties/integration/UserSettingsIntegration';
import { hasGlobalPropertyService } from '../../modules/Properties/reactive';
import type { Subscription } from '@pointorama/potree-shared';
import { attachNumberArrows } from '../../utils/numberScrub';

/**
 * Panel palette, per theme. Applied as CSS custom properties (`--tsp-*`) on
 * the panel root, so a live theme flip only updates the variables (plus a
 * redraw of the canvas thumbnails, which can't reference CSS variables).
 * Accents mirror DistanceCalculation's dark/light chart pairs.
 */
const PANEL_PALETTES = {
  dark: {
    bg: 'rgb(33, 33, 33)',
    text: '#ffffff',
    label: '#C2C6C8',
    faint: '#9ca3af',
    strong: '#d1d5db',
    border: '#444444',
    inputBg: '#1a1a1a',
    inputText: '#ffffff',
    selectBg: '#333333',
    checkboxBg: '#374151',
    checkboxBorder: '#4B5563',
    accent: '#B5FFA1',
    knobOn: '#2F5954',
    trackOff: '#404040',
    knobOff: '#9ca3af',
    cardSelectedBg: '#2F5954',
    unit: '#888888',
  },
  light: {
    bg: '#ffffff',
    text: '#1f2937',
    label: '#4b5563',
    faint: '#6b7280',
    strong: '#374151',
    border: '#d1d5db',
    inputBg: '#f9fafb',
    inputText: '#1f2937',
    selectBg: '#ffffff',
    checkboxBg: '#f9fafb',
    checkboxBorder: '#d1d5db',
    accent: '#5BCE3B',
    knobOn: '#2F5954',
    trackOff: '#d1d5db',
    knobOff: '#ffffff',
    cardSelectedBg: '#dcfce7',
    unit: '#6b7280',
  },
} as const;

type PanelPalette = (typeof PANEL_PALETTES)['dark'] | (typeof PANEL_PALETTES)['light'];

/** Cards kept in a library's "Recent" row. */
const RECENT_LIMIT = 6;

export interface ToolSettingsPanelOptions {
  title?: string;
  onSettingChange?: (event: ToolSettingsChangeEvent) => void;
  onClose?: () => void;
  position?: { top?: string; right?: string; bottom?: string; left?: string };
  /**
   * Translates user-facing schema strings (labels, placeholders) for display.
   * Defaults to identity — the source string is shown as-is.
   */
  translate?: (source: string) => string;
}

/**
 * A popover panel for displaying and editing tool settings.
 * Automatically generates UI controls based on the provided settings schema.
 */
export class ToolSettingsPanel {
  private element: HTMLDivElement;
  private contentContainer: HTMLDivElement;
  private schema: ToolSettingsSchema;
  private values: ToolSettingsValues;
  private onSettingChange?: (event: ToolSettingsChangeEvent) => void;
  private onClose?: () => void;
  private isVisible = false;
  private groupContainers: Map<string, HTMLElement> = new Map();
  /** Per-library-setting selection-highlight refreshers, so setValue can sync the cards. */
  private libraryRefreshers: Map<string, () => void> = new Map();
  /** Library thumbnails, kept so a theme flip can redraw their canvases. */
  private thumbnails: Array<{ canvas: HTMLCanvasElement; segments: number[] }> = [];
  private palette: PanelPalette;
  private themeSubscription: Subscription | null = null;
  private translate: (source: string) => string;
  constructor(schema: ToolSettingsSchema, options: ToolSettingsPanelOptions = {}) {
    this.schema = schema;
    this.onSettingChange = options.onSettingChange;
    this.onClose = options.onClose;
    this.translate = options.translate ?? ((source) => source);

    // Initialize values with defaults
    this.values = {};
    Object.entries(schema).forEach(([key, definition]) => {
      this.values[key] = definition.defaultValue;
    });

    // Create main container - matching RendererRightPanel style
    this.element = document.createElement('div');
    this.element.className = 'tool-settings-panel';
    this.element.style.cssText = `
      position: absolute;
      ${options.position?.top ? `top: ${options.position.top};` : 'top: 30px;'}
      ${options.position?.right ? `right: ${options.position.right};` : 'right: 10px;'}
      ${options.position?.bottom ? `bottom: ${options.position.bottom};` : ''}
      ${options.position?.left ? `left: ${options.position.left};` : ''}
      background: var(--tsp-bg);
      color: var(--tsp-text);
      border: 1px solid var(--tsp-border);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      padding: 16px;
      border-radius: 5px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      display: none;
      min-width: 200px;
      z-index: 1000;
      max-height: 80vh;
      overflow-y: auto;
      pointer-events: auto;
    `;
    // Initial theme from the DOM class (always correct, service or not);
    // applied after the cssText assignment above, which would wipe the vars.
    this.palette = document.documentElement.classList.contains('dark') ? PANEL_PALETTES.dark : PANEL_PALETTES.light;
    this.applyPalette();
    // Live theme switching while the panel exists.
    if (hasGlobalPropertyService()) {
      this.themeSubscription = getUserDarkModeProperty().subscribeToChanges((dark) => {
        this.palette = dark === true ? PANEL_PALETTES.dark : PANEL_PALETTES.light;
        this.applyPalette();
      });
    }

    // Prevent events from propagating to the viewer
    // Note: Don't stop mousemove as it interferes with slider dragging
    this.element.addEventListener('mousedown', (e) => e.stopPropagation());
    this.element.addEventListener('mouseup', (e) => e.stopPropagation());
    this.element.addEventListener('click', (e) => e.stopPropagation());
    this.element.addEventListener('dblclick', (e) => e.stopPropagation());
    this.element.addEventListener('wheel', (e) => e.stopPropagation());
    this.element.addEventListener('contextmenu', (e) => e.stopPropagation());

    // Create content container
    this.contentContainer = document.createElement('div');
    this.contentContainer.style.cssText = `
      display: block;
    `;
    this.element.appendChild(this.contentContainer);

    // Generate UI controls
    this.generateControls();
  }

  /** Write the palette into the panel's CSS variables and redraw the canvas
   *  thumbnails (canvases can't reference CSS variables). */
  private applyPalette(): void {
    const style = this.element.style;
    style.setProperty('--tsp-bg', this.palette.bg);
    style.setProperty('--tsp-text', this.palette.text);
    style.setProperty('--tsp-label', this.palette.label);
    style.setProperty('--tsp-faint', this.palette.faint);
    style.setProperty('--tsp-strong', this.palette.strong);
    style.setProperty('--tsp-border', this.palette.border);
    style.setProperty('--tsp-input-bg', this.palette.inputBg);
    style.setProperty('--tsp-input-text', this.palette.inputText);
    style.setProperty('--tsp-select-bg', this.palette.selectBg);
    style.setProperty('--tsp-checkbox-bg', this.palette.checkboxBg);
    style.setProperty('--tsp-checkbox-border', this.palette.checkboxBorder);
    style.setProperty('--tsp-accent', this.palette.accent);
    style.setProperty('--tsp-knob-on', this.palette.knobOn);
    style.setProperty('--tsp-track-off', this.palette.trackOff);
    style.setProperty('--tsp-knob-off', this.palette.knobOff);
    style.setProperty('--tsp-card-selected-bg', this.palette.cardSelectedBg);
    style.setProperty('--tsp-unit', this.palette.unit);
    for (const { canvas, segments } of this.thumbnails) {
      this.drawLibraryThumbnail(canvas, segments);
    }
  }

  private generateControls(): void {
    // First pass: create categories and ungrouped controls
    Object.entries(this.schema).forEach(([key, definition]) => {
      // Skip grouped items in first pass
      if ('group' in definition && definition.group) {
        return;
      }

      if (definition.type === 'category') {
        const categoryControl = this.createCategoryControl(key, definition);
        this.contentContainer.appendChild(categoryControl);
      } else {
        const control = this.createControl(key, definition);
        this.contentContainer.appendChild(control);
      }
    });

    // Second pass: add grouped controls to their categories
    Object.entries(this.schema).forEach(([key, definition]) => {
      if ('group' in definition && definition.group) {
        const groupContainer = this.groupContainers.get(definition.group);
        if (groupContainer) {
          const control = this.createControl(key, definition, definition.group);
          groupContainer.appendChild(control);
        }
      }
    });
  }

  private createCategoryControl(key: string, definition: Extract<ToolSettingDefinition, { type: 'category' }>): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      margin-bottom: 0;
    `;

    // Divider line above category
    const divider = document.createElement('div');
    divider.style.cssText = `
      border-top: 1px solid var(--tsp-border);
      margin: 16px 0 0 0;
    `;
    container.appendChild(divider);

    // Category header with checkbox on right (like PropertiesSection)
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 0 8px 0;
    `;

    // Label
    const label = document.createElement('span');
    label.style.cssText = `
      font-weight: bold;
      font-size: 14px;
      color: var(--tsp-text);
    `;
    label.textContent = this.translate(definition.label);

    // Checkbox (on the right, styled like existing property panel)
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = definition.defaultValue;
    checkbox.style.cssText = `
      width: 16px;
      height: 16px;
      accent-color: #5BCE3B;
      background-color: var(--tsp-checkbox-bg);
      border: 1px solid var(--tsp-checkbox-border);
      border-radius: 4px;
      cursor: pointer;
      margin-right: 8px;
    `;

    header.appendChild(label);
    header.appendChild(checkbox);
    container.appendChild(header);

    // Group content container (for child controls) - no indent
    const groupContent = document.createElement('div');
    groupContent.style.cssText = `
      margin-top: 0;
      opacity: ${definition.defaultValue ? '1' : '0.5'};
      pointer-events: ${definition.defaultValue ? 'auto' : 'none'};
      transition: opacity 0.2s;
    `;
    container.appendChild(groupContent);

    // Store reference for grouped controls
    this.groupContainers.set(key, groupContent);

    // Handle checkbox change
    checkbox.addEventListener('change', () => {
      const isEnabled = checkbox.checked;
      groupContent.style.opacity = isEnabled ? '1' : '0.5';
      groupContent.style.pointerEvents = isEnabled ? 'auto' : 'none';
      this.updateValue(key, isEnabled);
    });

    // Make label clickable too
    label.style.cursor = 'pointer';
    label.addEventListener('click', () => {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change'));
    });

    return container;
  }

  private createControl(key: string, definition: ToolSettingDefinition, _group?: string): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      margin-bottom: 8px;
    `;

    // Label (skip for category type which has its own label)
    if (definition.type !== 'category') {
      const label = document.createElement('label');
      label.style.cssText = `
        display: block;
        margin-bottom: 4px;
        font-weight: normal;
        font-size: 14px;
        color: var(--tsp-label);
      `;
      label.textContent = this.translate(definition.label);
      container.appendChild(label);
    }

    // Control input
    const inputContainer = document.createElement('div');
    let input: HTMLElement;

    switch (definition.type) {
      case 'slider':
        input = this.createSliderInput(key, definition);
        break;
      case 'toggle':
        input = this.createToggleInput(key, definition);
        break;
      case 'select':
        input = this.createSelectInput(key, definition);
        break;
      case 'number':
        input = this.createNumberInput(key, definition);
        break;
      case 'text':
        input = this.createTextInput(key, definition);
        break;
      case 'color':
        input = this.createColorInput(key, definition);
        break;
      case 'library':
        input = this.createLibraryInput(key, definition);
        break;
      case 'category':
        // Categories are handled separately
        return container;
    }

    inputContainer.appendChild(input);
    container.appendChild(inputContainer);

    return container;
  }

  private createSliderInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'slider' }>): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
    `;

    // Spacing above slider
    const topSpacer = document.createElement('div');
    topSpacer.style.height = '8px';
    container.appendChild(topSpacer);

    // Slider input
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(definition.min);
    slider.max = String(definition.max);
    slider.step = String(definition.step || 0.1);
    slider.value = String(definition.defaultValue);
    slider.style.cssText = `
      width: 100%;
      cursor: pointer;
      accent-color: var(--tsp-accent);
      height: 8px;
      margin: 0;
    `;

    // Capture pointer to ensure we get all mouse events during drag
    slider.addEventListener('pointerdown', (e) => {
      slider.setPointerCapture(e.pointerId);
    });

    container.appendChild(slider);

    // Spacing below slider
    const bottomSpacer = document.createElement('div');
    bottomSpacer.style.height = '8px';
    container.appendChild(bottomSpacer);

    // Labels container - min, current value, max
    const labelsContainer = document.createElement('div');
    labelsContainer.style.cssText = `
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: var(--tsp-faint);
    `;

    // Determine decimal places: use explicit decimals if provided, otherwise derive from step
    const step = definition.step || 0.1;
    const decimalPlaces = definition.decimals ?? (step >= 1 ? 0 : Math.max(0, -Math.floor(Math.log10(step))));

    const minLabel = document.createElement('span');
    minLabel.textContent = Number(definition.min).toFixed(decimalPlaces);

    const valueDisplay = document.createElement('span');
    valueDisplay.style.cssText = `
      font-weight: 500;
      color: var(--tsp-strong);
    `;
    valueDisplay.textContent = `${definition.defaultValue.toFixed(decimalPlaces)}${definition.unit || ''}`;

    const maxLabel = document.createElement('span');
    maxLabel.textContent = Number(definition.max).toFixed(decimalPlaces);

    slider.addEventListener('input', () => {
      const value = parseFloat(slider.value);
      valueDisplay.textContent = `${value.toFixed(decimalPlaces)}${definition.unit || ''}`;
      this.updateValue(key, value);
    });

    labelsContainer.appendChild(minLabel);
    labelsContainer.appendChild(valueDisplay);
    labelsContainer.appendChild(maxLabel);
    container.appendChild(labelsContainer);

    return container;
  }

  private createToggleInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'toggle' }>): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      align-items: center;
    `;

    // Create toggle switch (like Headless UI Switch)
    const toggleSwitch = document.createElement('button');
    toggleSwitch.type = 'button';
    const isChecked = definition.defaultValue;
    toggleSwitch.style.cssText = `
      position: relative;
      display: inline-flex;
      height: 24px;
      width: 44px;
      align-items: center;
      border-radius: 9999px;
      background-color: ${isChecked ? 'var(--tsp-accent)' : 'var(--tsp-track-off)'};
      cursor: pointer;
      border: none;
      outline: none;
      transition: background-color 0.2s;
    `;

    const toggleKnob = document.createElement('span');
    toggleKnob.style.cssText = `
      display: inline-block;
      height: 16px;
      width: 16px;
      border-radius: 9999px;
      background-color: ${isChecked ? 'var(--tsp-knob-on)' : 'var(--tsp-knob-off)'};
      transform: ${isChecked ? 'translateX(24px)' : 'translateX(4px)'};
      transition: transform 0.2s, background-color 0.2s;
    `;

    toggleSwitch.appendChild(toggleKnob);

    let currentState = isChecked;

    toggleSwitch.addEventListener('click', () => {
      currentState = !currentState;
      toggleSwitch.style.backgroundColor = currentState ? 'var(--tsp-accent)' : 'var(--tsp-track-off)';
      toggleKnob.style.backgroundColor = currentState ? 'var(--tsp-knob-on)' : 'var(--tsp-knob-off)';
      toggleKnob.style.transform = currentState ? 'translateX(24px)' : 'translateX(4px)';
      this.updateValue(key, currentState);
    });

    container.appendChild(toggleSwitch);

    return container;
  }

  private createSelectInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'select' }>): HTMLElement {
    const select = document.createElement('select');
    select.style.cssText = `
      width: 100%;
      padding: 4px 6px;
      background: var(--tsp-select-bg);
      border: 1px solid var(--tsp-border);
      border-radius: 3px;
      color: var(--tsp-input-text);
      font-size: 12px;
      font-family: monospace;
      text-align: center;
      text-align-last: center;
      cursor: pointer;
      outline: none;
    `;

    definition.options.forEach((option: { label: string; value: string }) => {
      const optionElement = document.createElement('option');
      optionElement.value = option.value;
      optionElement.textContent = this.translate(option.label);
      optionElement.style.cssText = `
        background: var(--tsp-select-bg);
        color: var(--tsp-input-text);
      `;
      select.appendChild(optionElement);
    });

    select.value = definition.defaultValue;

    select.addEventListener('change', () => {
      this.updateValue(key, select.value);
    });

    return select;
  }

  private createNumberInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'number' }>): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
    `;

    const input = document.createElement('input');
    input.type = 'number';
    input.value = String(definition.defaultValue);
    if (definition.min !== undefined) input.min = String(definition.min);
    if (definition.max !== undefined) input.max = String(definition.max);
    if (definition.step !== undefined) input.step = String(definition.step);
    input.style.cssText = `
      width: 100%;
      padding: 4px 18px;
      background: var(--tsp-input-bg);
      border: 1px solid var(--tsp-border);
      border-radius: 3px;
      color: var(--tsp-input-text);
      font-size: 12px;
      font-family: monospace;
      outline: none;
    `;

    if (definition.unit) {
      const unit = document.createElement('span');
      unit.style.cssText = `
        color: var(--tsp-unit);
        font-size: 11px;
      `;
      unit.textContent = definition.unit;
      wrapper.appendChild(unit);
    }

    input.addEventListener('change', () => {
      this.updateValue(key, parseFloat(input.value));
    });

    // Blender-style: hover ‹ › step arrows; dragging scrubs (utils/numberScrub.ts, installed app-wide).
    const field = attachNumberArrows(input);
    field.style.flex = '1';
    wrapper.insertBefore(field, wrapper.firstChild);

    return wrapper;
  }

  private createTextInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'text' }>): HTMLElement {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = definition.defaultValue;
    if (definition.placeholder) input.placeholder = this.translate(definition.placeholder);
    input.style.cssText = `
      width: 100%;
      padding: 4px 6px;
      background: var(--tsp-input-bg);
      border: 1px solid var(--tsp-border);
      border-radius: 3px;
      color: var(--tsp-input-text);
      font-size: 12px;
      font-family: monospace;
      outline: none;
    `;

    input.addEventListener('change', () => {
      this.updateValue(key, input.value);
    });

    return input;
  }

  private createColorInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'color' }>): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const input = document.createElement('input');
    input.type = 'color';
    input.value = definition.defaultValue;
    input.style.cssText = `
      width: 40px;
      height: 24px;
      border: 1px solid var(--tsp-border);
      border-radius: 3px;
      cursor: pointer;
      background: transparent;
    `;

    const valueDisplay = document.createElement('span');
    valueDisplay.style.cssText = `
      font-family: monospace;
      font-size: 12px;
      color: var(--tsp-unit);
    `;
    valueDisplay.textContent = definition.defaultValue;

    input.addEventListener('change', () => {
      valueDisplay.textContent = input.value;
      this.updateValue(key, input.value);
    });

    wrapper.appendChild(input);
    wrapper.appendChild(valueDisplay);

    return wrapper;
  }

  /**
   * The object library: search, category chips, an optional "Recent" row and
   * a card grid per category. Clicking a card selects its value; clicking the
   * selected card deselects (value '').
   */
  private createLibraryInput(key: string, definition: Extract<ToolSettingDefinition, { type: 'library' }>): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 272px;
    `;

    const cards: Array<{ element: HTMLButtonElement; value: string }> = [];
    const refreshSelection = (): void => {
      const selected = String(this.values[key] ?? '');
      for (const card of cards) {
        const isSelected = card.value === selected;
        card.element.style.borderColor = isSelected ? 'var(--tsp-accent)' : 'var(--tsp-border)';
        card.element.style.background = isSelected ? 'var(--tsp-card-selected-bg)' : 'var(--tsp-input-bg)';
      }
    };
    this.libraryRefreshers.set(key, refreshSelection);

    const items = new Map(definition.categories.flatMap((category) => category.items.map((item) => [item.value, item] as const)));
    const readRecents = (): string[] => {
      if (!definition.recentStorageKey) return [];
      try {
        const parsed: unknown = JSON.parse(localStorage.getItem(definition.recentStorageKey) ?? '[]');
        return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string' && items.has(v)) : [];
      } catch {
        return [];
      }
    };
    const pushRecent = (value: string): void => {
      if (!definition.recentStorageKey || !value) return;
      try {
        localStorage.setItem(definition.recentStorageKey, JSON.stringify([value, ...readRecents().filter((v) => v !== value)].slice(0, RECENT_LIMIT)));
      } catch {
        /* storage unavailable: recents are a convenience */
      }
    };

    const makeCard = (item: LibraryItemDefinition): HTMLButtonElement => {
      const card = document.createElement('button');
      card.type = 'button';
      card.title = this.translate(item.label);
      card.style.cssText = `
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        padding: 6px 4px;
        background: var(--tsp-input-bg);
        border: 1px solid var(--tsp-border);
        border-radius: 6px;
        cursor: pointer;
        color: var(--tsp-label);
        font-size: 11px;
        transition: border-color 0.15s, background 0.15s;
      `;
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 56;
      if (item.thumbnailSegments && item.thumbnailSegments.length >= 6) {
        this.drawLibraryThumbnail(canvas, item.thumbnailSegments);
        this.thumbnails.push({ canvas, segments: item.thumbnailSegments });
      }
      card.appendChild(canvas);
      const label = document.createElement('span');
      label.textContent = this.translate(item.label);
      label.style.cssText = `
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      `;
      card.appendChild(label);
      if (item.badge) {
        const badge = document.createElement('span');
        badge.textContent = this.translate(item.badge);
        badge.style.cssText = `
          position: absolute;
          top: 3px;
          right: 3px;
          padding: 0 4px;
          border-radius: 6px;
          font-size: 9px;
          line-height: 14px;
          background: var(--tsp-accent);
          color: #1f2937;
        `;
        card.appendChild(badge);
      }
      card.addEventListener('click', () => {
        const next = String(this.values[key] ?? '') === item.value ? '' : item.value;
        this.updateValue(key, next);
        pushRecent(next);
        refreshSelection();
      });
      cards.push({ element: card, value: item.value });
      return card;
    };

    const makeGrid = (): HTMLDivElement => {
      const grid = document.createElement('div');
      grid.style.cssText = `
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
      `;
      return grid;
    };
    const makeHeader = (text: string): HTMLDivElement => {
      const header = document.createElement('div');
      header.style.cssText = `
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--tsp-faint);
        margin: 2px 0 4px;
      `;
      header.textContent = text;
      return header;
    };

    let query = '';
    let activeCategory: string | null = null;
    const matches = (item: LibraryItemDefinition): boolean => {
      if (!query) return true;
      const haystack = [this.translate(item.label), item.label, ...(item.keywords ?? [])].join(' ').toLowerCase();
      return query.split(/\s+/).every((word) => haystack.includes(word));
    };

    if (definition.searchable !== false) {
      const search = document.createElement('input');
      search.type = 'search';
      search.placeholder = this.translate('search objects…');
      search.style.cssText = `
        width: 100%;
        box-sizing: border-box;
        padding: 6px 8px;
        background: var(--tsp-input-bg);
        border: 1px solid var(--tsp-border);
        border-radius: 6px;
        color: var(--tsp-input-text);
        font-size: 12px;
        outline: none;
      `;
      search.addEventListener('input', () => {
        query = search.value.trim().toLowerCase();
        render();
      });
      container.appendChild(search);
    }

    // Category chips: "All" plus one per category; a second click returns to All.
    const chips = document.createElement('div');
    chips.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    `;
    const chipButtons: Array<{ element: HTMLButtonElement; category: string | null }> = [];
    const refreshChips = (): void => {
      for (const chip of chipButtons) {
        const active = chip.category === activeCategory;
        chip.element.style.background = active ? 'var(--tsp-card-selected-bg)' : 'transparent';
        chip.element.style.borderColor = active ? 'var(--tsp-accent)' : 'var(--tsp-border)';
        chip.element.style.color = active ? 'var(--tsp-text)' : 'var(--tsp-label)';
      }
    };
    const addChip = (label: string, category: string | null): void => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.textContent = label;
      chip.style.cssText = `
        padding: 2px 8px;
        border: 1px solid var(--tsp-border);
        border-radius: 999px;
        font-size: 11px;
        cursor: pointer;
        background: transparent;
      `;
      chip.addEventListener('click', () => {
        activeCategory = activeCategory === category ? null : category;
        refreshChips();
        render();
      });
      chipButtons.push({ element: chip, category });
      chips.appendChild(chip);
    };
    if (definition.categories.length > 1) {
      addChip(this.translate('All'), null);
      for (const category of definition.categories) addChip(this.translate(category.label), category.label);
      container.appendChild(chips);
      refreshChips();
    }

    const scroll = document.createElement('div');
    scroll.style.cssText = `
      max-height: 360px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 10px;
    `;
    container.appendChild(scroll);

    const render = (): void => {
      cards.length = 0;
      this.thumbnails = this.thumbnails.filter((thumb) => !scroll.contains(thumb.canvas));
      scroll.replaceChildren();

      const recents = !query && activeCategory === null ? readRecents() : [];
      if (recents.length > 0) {
        const section = document.createElement('div');
        section.appendChild(makeHeader(this.translate('Recent')));
        const grid = makeGrid();
        for (const value of recents) {
          const item = items.get(value);
          if (item) grid.appendChild(makeCard(item));
        }
        section.appendChild(grid);
        scroll.appendChild(section);
      }

      let shown = 0;
      for (const category of definition.categories) {
        if (activeCategory !== null && category.label !== activeCategory) continue;
        const visibleItems = category.items.filter(matches);
        if (visibleItems.length === 0) continue;
        const section = document.createElement('div');
        section.appendChild(makeHeader(this.translate(category.label)));
        const grid = makeGrid();
        for (const item of visibleItems) grid.appendChild(makeCard(item));
        section.appendChild(grid);
        scroll.appendChild(section);
        shown += visibleItems.length;
      }

      if (shown === 0) {
        const empty = document.createElement('div');
        empty.style.cssText = `
          padding: 16px 4px;
          text-align: center;
          font-size: 12px;
          color: var(--tsp-faint);
        `;
        empty.textContent = this.translate('No objects match your search');
        scroll.appendChild(empty);
      }
      refreshSelection();
    };
    render();

    if (definition.footerAction) {
      const { label, onClick } = definition.footerAction;
      const footer = document.createElement('button');
      footer.type = 'button';
      footer.textContent = this.translate(label);
      footer.style.cssText = `
        align-self: flex-start;
        padding: 2px 0;
        border: none;
        background: transparent;
        color: var(--tsp-accent);
        font-size: 12px;
        cursor: pointer;
      `;
      footer.addEventListener('click', onClick);
      container.appendChild(footer);
    }

    return container;
  }

  /** Stroke flat [x,y,z,...] outline segments fitted (with padding) into a
   *  card canvas. Cleared first so theme-flip redraws don't stack strokes. */
  private drawLibraryThumbnail(canvas: HTMLCanvasElement, segments: number[]): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i + 1 < segments.length; i += 3) {
      minX = Math.min(minX, segments[i]);
      maxX = Math.max(maxX, segments[i]);
      minY = Math.min(minY, segments[i + 1]);
      maxY = Math.max(maxY, segments[i + 1]);
    }
    if (!isFinite(minX)) return;
    const pad = 5;
    const scale = Math.min((canvas.width - 2 * pad) / Math.max(maxX - minX, 1e-6), (canvas.height - 2 * pad) / Math.max(maxY - minY, 1e-6));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const toPx = (x: number, y: number): [number, number] => [canvas.width / 2 + (x - cx) * scale, canvas.height / 2 - (y - cy) * scale];

    ctx.strokeStyle = this.palette.label;
    ctx.lineWidth = 1.25;
    ctx.beginPath();
    for (let i = 0; i + 5 < segments.length; i += 6) {
      const [ax, ay] = toPx(segments[i], segments[i + 1]);
      const [bx, by] = toPx(segments[i + 3], segments[i + 4]);
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
    }
    ctx.stroke();
  }

  private updateValue(key: string, value: ToolSettingValue): void {
    this.values[key] = value;

    if (this.onSettingChange) {
      this.onSettingChange({
        key,
        value,
        allValues: { ...this.values },
      });
    }
  }

  public getValue(key: string): ToolSettingValue | undefined {
    return this.values[key];
  }

  public getAllValues(): ToolSettingsValues {
    return { ...this.values };
  }

  public setValue(key: string, value: ToolSettingValue): void {
    this.values[key] = value;
    // Library cards re-read the value for their selection highlight; the other
    // control types don't support external updates yet.
    this.libraryRefreshers.get(key)?.();
  }

  /**
   * Replace the schema and regenerate the controls in place. Used when a
   * setting's presentation depends on external state that can change while the
   * panel is open (e.g. the active measurement unit, which rescales a slider's
   * value/min/max/step + unit suffix). The DOM is rebuilt from `schema`'s
   * defaults — callers that need to preserve a live value should bake it into
   * the new schema's `defaultValue` before calling.
   */
  public rebuild(schema: ToolSettingsSchema): void {
    this.schema = schema;
    this.values = {};
    Object.entries(schema).forEach(([key, definition]) => {
      this.values[key] = definition.defaultValue;
    });
    this.groupContainers.clear();
    this.libraryRefreshers.clear();
    this.thumbnails = [];
    this.contentContainer.replaceChildren();
    this.generateControls();
  }

  public setOnClose(callback: () => void): void {
    this.onClose = callback;
  }

  public show(): void {
    this.element.style.display = 'block';
    this.isVisible = true;
  }

  public hide(): void {
    this.element.style.display = 'none';
    this.isVisible = false;
  }

  public toggle(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  public getElement(): HTMLElement {
    return this.element;
  }

  public destroy(): void {
    this.themeSubscription?.unsubscribe();
    this.themeSubscription = null;
    this.element.remove();
  }
}
